/**
 * Shared country utilities
 * Uses countries-list package for complete ISO 3166-1 country data
 * All components should import from here for consistent country handling
 */
import { countries, languages as ISO_LANGUAGES } from 'countries-list';

export interface Country {
  code: string;
  name: string;
  native: string;
  flag: string;
  phone: number;
  currency: string[];
  languages: string[];
}

export interface CountryOption {
  country_code: string;
  country_name: string;
  flag: string;
}

/**
 * Convert country code to flag emoji using Unicode regional indicators
 */
export function getFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '🏳️';

  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}

/**
 * IBAN countries - countries that use IBAN for bank transfers
 * Includes all EU/EEA countries plus Switzerland and UK
 * Source: https://www.iban.com/structure
 */
export const IBAN_COUNTRY_CODES = new Set([
  // EU Member States
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DE', // Germany
  'DK', // Denmark
  'EE', // Estonia
  'ES', // Spain
  'FI', // Finland
  'FR', // France
  'GR', // Greece
  'HR', // Croatia
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LT', // Lithuania
  'LU', // Luxembourg
  'LV', // Latvia
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SE', // Sweden
  'SI', // Slovenia
  'SK', // Slovakia
  // EEA (non-EU)
  'IS', // Iceland
  'LI', // Liechtenstein
  'NO', // Norway
  // Other IBAN countries
  'CH', // Switzerland
  'GB', // United Kingdom (uses IBAN but also sort code/account)
  'MC', // Monaco
  'SM', // San Marino
  'VA', // Vatican City
  'AD', // Andorra
  'AL', // Albania
  'BA', // Bosnia and Herzegovina
  'ME', // Montenegro
  'MK', // North Macedonia
  'RS', // Serbia
  'XK', // Kosovo
  'MD', // Moldova
  'UA', // Ukraine
  'BY', // Belarus
  'GE', // Georgia
  'TR', // Turkey
  'IL', // Israel
  'SA', // Saudi Arabia
  'AE', // United Arab Emirates
  'QA', // Qatar
  'KW', // Kuwait
  'BH', // Bahrain
  'JO', // Jordan
  'PS', // Palestine
  'LB', // Lebanon
  'EG', // Egypt
  'TN', // Tunisia
  'MR', // Mauritania
  'BR', // Brazil
]);

/**
 * Check if a country uses IBAN for bank transfers
 */
export function isIbanCountry(countryCode: string): boolean {
  return IBAN_COUNTRY_CODES.has(countryCode.toUpperCase());
}

/**
 * Countries that commonly require VAT numbers for freelancers/businesses
 * These countries have VAT systems that apply to self-employed individuals
 */
export const VAT_COUNTRY_CODES = new Set([
  // EU countries with VAT systems applicable to freelancers
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DE', // Germany
  'DK', // Denmark
  'EE', // Estonia
  'ES', // Spain
  'FI', // Finland
  'FR', // France
  'GR', // Greece
  'HR', // Croatia
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LT', // Lithuania
  'LU', // Luxembourg
  'LV', // Latvia
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SE', // Sweden
  'SI', // Slovenia
  'SK', // Slovakia
  // Non-EU with VAT
  'CH', // Switzerland (MWST)
  'NO', // Norway
  'IS', // Iceland
  'GB', // United Kingdom (VAT)
]);

/**
 * Check if a country commonly requires VAT numbers
 */
export function isVatCountry(countryCode: string): boolean {
  return VAT_COUNTRY_CODES.has(countryCode.toUpperCase());
}

/**
 * Countries that use routing number + account number (US banking system)
 */
export const US_BANKING_COUNTRY_CODES = new Set(['US']);

/**
 * Check if a country uses US-style banking (routing + account number)
 */
export function isUsBankingCountry(countryCode: string): boolean {
  return US_BANKING_COUNTRY_CODES.has(countryCode.toUpperCase());
}

/**
 * Countries that use sort code + account number (UK banking system)
 */
export const UK_BANKING_COUNTRY_CODES = new Set(['GB']);

/**
 * Check if a country uses UK-style banking (sort code + account number)
 * Note: UK also supports IBAN but commonly uses sort code format
 */
export function isUkBankingCountry(countryCode: string): boolean {
  return UK_BANKING_COUNTRY_CODES.has(countryCode.toUpperCase());
}

/**
 * Priority countries for dance industry (shown first in list)
 * These are the most common countries where dancers work
 */
export const PRIORITY_COUNTRY_CODES = [
  'CH', // Switzerland
  'FR', // France
  'DE', // Germany
  'IT', // Italy
  'AT', // Austria
  'BE', // Belgium
  'NL', // Netherlands
  'ES', // Spain
  'PT', // Portugal
  'GB', // United Kingdom
  'IE', // Ireland
  'US', // United States
  'CA', // Canada
  'AU', // Australia
  'NZ', // New Zealand
  'JP', // Japan
  'KR', // South Korea
  'SG', // Singapore
  'BR', // Brazil
  'AR', // Argentina
  'MX', // Mexico
  'CZ', // Czech Republic
  'PL', // Poland
  'HU', // Hungary
  'RU', // Russia
  'UA', // Ukraine
];

/**
 * Transform countries-list data to our Country format
 */
export const ALL_COUNTRIES: Country[] = Object.entries(countries).map(
  ([code, country]) => ({
    code,
    name: country.name,
    native: country.native,
    flag: getFlagEmoji(code),
    phone: country.phone[0] ?? 0,
    currency: country.currency,
    languages: country.languages,
  }),
);

/**
 * Get all countries sorted with priority ones first, then alphabetically
 */
export function getSortedCountries(): Country[] {
  const priority = ALL_COUNTRIES.filter((c) =>
    PRIORITY_COUNTRY_CODES.includes(c.code),
  ).sort(
    (a, b) =>
      PRIORITY_COUNTRY_CODES.indexOf(a.code) -
      PRIORITY_COUNTRY_CODES.indexOf(b.code),
  );

  const rest = ALL_COUNTRIES.filter(
    (c) => !PRIORITY_COUNTRY_CODES.includes(c.code),
  ).sort((a, b) => a.name.localeCompare(b.name));

  return [...priority, ...rest];
}

/**
 * Get all countries as CountryOption format (for CountrySelector compatibility)
 */
export function getAllCountryOptions(): CountryOption[] {
  return getSortedCountries().map((country) => ({
    country_code: country.code,
    country_name: country.name,
    flag: country.flag,
  }));
}

/**
 * Countries as generic `{ value, label }` form options (value = ISO 3166-1
 * alpha-2 code, label = flag + English name). For structure-driven `select` /
 * `multiselect` fields that store a country code and need a populated dropdown
 * without inlining the full country list in the form structure.
 */
export function getCountryFieldOptions(): { value: string; label: string }[] {
  return getSortedCountries().map((country) => ({
    value: country.code,
    label: `${country.flag} ${country.name}`,
  }));
}

/**
 * ISO 639-1 language option, alphabetised by English name. Filters out
 * codes whose `name` is empty (a few entries in the `countries-list`
 * package are placeholders).
 */
export interface LanguageOption {
  code: string;
  name: string;
  native: string;
}

const ALL_LANGUAGES: LanguageOption[] = (() => {
  const list: LanguageOption[] = [];
  for (const [code, info] of Object.entries(ISO_LANGUAGES)) {
    if (!info?.name) continue;
    list.push({ code, name: info.name, native: info.native ?? info.name });
  }
  list.sort((a, b) => a.name.localeCompare(b.name));
  return list;
})();

/**
 * Get all ISO 639-1 languages as `{ code, name, native }`, sorted by name.
 * Used by `LanguagePicker` for the wizard's About-You step.
 */
export function getAllLanguageOptions(): LanguageOption[] {
  return ALL_LANGUAGES;
}

/**
 * Look up a language's English name from its ISO 639-1 code.
 */
export function getLanguageName(code: string): string {
  return ALL_LANGUAGES.find((l) => l.code === code)?.name ?? code;
}

/**
 * Get country by code
 */
export function getCountryByCode(code: string): Country | undefined {
  return ALL_COUNTRIES.find((c) => c.code.toUpperCase() === code.toUpperCase());
}

/**
 * Get country name by code
 */
export function getCountryName(code: string): string {
  const country = getCountryByCode(code);
  return country?.name ?? code;
}

/**
 * Search countries by name or code
 */
export function searchCountries(query: string): Country[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return getSortedCountries();

  return getSortedCountries().filter(
    (country) =>
      country.name.toLowerCase().includes(normalizedQuery) ||
      country.native.toLowerCase().includes(normalizedQuery) ||
      country.code.toLowerCase() === normalizedQuery,
  );
}

// Re-export for backwards compatibility
